import type { ReactNode } from "react";
import MeritoCheckbox from "../MeritoCheckbox/MeritoCheckbox";
import styles from "./SelectAllList.module.scss";

export interface SelectAllListItem {
  id: string;
  label: ReactNode;
  locked?: boolean;
  children?: SelectAllListItem[];
}

export interface SelectAllListProps {
  items: SelectAllListItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

type CheckState = "yes" | "no" | "partial";

const leavesOf = (item: SelectAllListItem): SelectAllListItem[] => (item.children?.length ? item.children : [item]);

const SelectAllList = ({ items, selectedIds, onChange, disabled = false }: SelectAllListProps): JSX.Element => {
  const selected = new Set(selectedIds);
  const selectableOf = (leaves: SelectAllListItem[]): string[] => leaves.filter((leaf) => !leaf.locked).map((leaf) => leaf.id);

  const stateOf = (ids: string[]): CheckState => {
    const count = ids.filter((id) => selected.has(id)).length;
    if (!ids.length || count === ids.length) return "yes";
    return count ? "partial" : "no";
  };

  const toggle = (ids: string[], state: CheckState): void => {
    const next = new Set(selectedIds);
    ids.forEach((id) => (state === "yes" ? next.delete(id) : next.add(id)));
    onChange(Array.from(next));
  };

  const allIds = items.flatMap((item) => selectableOf(leavesOf(item)));
  const allState = stateOf(allIds);
  const groupCount = items.filter((item) => selectableOf(leavesOf(item)).length).length;

  const row = (item: SelectAllListItem, ids: string[]): JSX.Element => {
    const state = stateOf(ids);
    return (
      <MeritoCheckbox
        checked={state}
        state={!ids.length || disabled ? "disabled" : "default"}
        label={<span className={styles.text}>{item.label}</span>}
        onChange={() => toggle(ids, state)}
      />
    );
  };

  return (
    <div className={styles.box}>
      {allIds.length ? (
        <MeritoCheckbox
          checked={allState}
          state={disabled ? "disabled" : "default"}
          label={<span className={styles.text}>{allState === "yes" ? "Deselect" : "Select"} All ({groupCount})</span>}
          onChange={() => toggle(allIds, allState)}
        />
      ) : null}
      <div className={styles.list}>
        {items.map((item) => (
          <div className={styles.group} key={item.id}>
            {row(item, selectableOf(leavesOf(item)))}
            {item.children?.length ? (
              <div className={styles.children}>
                {item.children.map((child) => (
                  <div key={child.id}>{row(child, child.locked ? [] : [child.id])}</div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SelectAllList;
